import { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { scrollToHash } from "@/lib/scroll";
import { setInquiryPrefill } from "@/lib/inquiryPrefill";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const PANEL_STYLES = {
  "brand-identity": {
    bg: "#1C0729", // deep midnight plum
    heading: "#f6f0e6",
    text: "rgba(233, 223, 240, 0.72)",
    eyebrow: "rgba(203, 184, 220, 0.7)",
    rule: "rgba(246, 240, 230, 0.14)",
    arrow: "rgba(246, 240, 230, 0.35)",
    chromePrice: true,
  },
  "websites-setup": {
    bg: "#EAC9CE", // soft blush pink
    heading: "#33122E",
    text: "rgba(51, 18, 46, 0.7)",
    eyebrow: "rgba(51, 18, 46, 0.55)",
    rule: "rgba(51, 18, 46, 0.16)",
    arrow: "rgba(51, 18, 46, 0.35)",
    chromePrice: false,
  },
  "design-creative": {
    bg: "#F3EDE3", // pearl
    heading: "#1c1a19",
    text: "rgba(28, 26, 25, 0.66)",
    eyebrow: "rgba(28, 26, 25, 0.5)",
    rule: "rgba(28, 26, 25, 0.15)",
    arrow: "rgba(28, 26, 25, 0.3)",
    chromePrice: false,
  },
};

const IRIDESCENT = "linear-gradient(100deg, #8fb3c4, #b3a3d4, #e3c2bd, #eed3ba, #9fc2d2, #c2b1dc)";

const CATEGORIES = [
  {
    id: "brand-identity",
    title: "Brand & Identity",
    tab: "Brand",
    services: [
      {
        id: "logo",
        name: "The Logo Edit",
        price: "Starting at $350",
        short: "A focused visual identity for a new business, project or idea.",
        desc: "A focused visual identity for a new business, project, product or creative idea.",
        includes: ["Primary logo development", "Secondary variation", "Submark/monogram where appropriate", "Basic color direction", "Final production files"],
      },
      {
        id: "identity",
        name: "The Identity Edit",
        price: "Starting at $850",
        short: "A complete identity system built for recognition and consistency.",
        desc: "A more complete visual identity designed to create recognition and consistency.",
        includes: ["Logo suite", "Color palette", "Typography direction", "Supporting visual language", "Social/profile marks", "Mini identity guide"],
      },
      {
        id: "brand",
        name: "The Brand Edit",
        price: "Starting at $1,650",
        short: "Strategy and a complete visual identity, developed together.",
        desc: "Strategic brand development paired with a complete visual identity.",
        includes: ["Discovery", "Positioning direction", "Creative direction", "Identity system", "Typography", "Color system", "Supporting graphics", "Brand guidelines"],
      },
      {
        id: "world",
        name: "The Brand World",
        price: "Starting at $2,950",
        short: "The larger visual world surrounding your brand — beyond the logo.",
        desc: "For brands that need more than an identity. This engagement develops the larger visual world surrounding the brand.",
        includes: ["Strategy", "Positioning", "Identity", "Expanded visual system", "Creative direction", "Branded applications", "Comprehensive brand guidelines"],
      },
      {
        id: "architecture",
        name: "The Brand Architecture Edit",
        price: "Starting at $4,000",
        short: "Structure for parent brands, sub-brands and growing ecosystems.",
        desc: "For parent companies, multi-brand organizations, product families, platforms and expanding ecosystems.",
        includes: ["Brand hierarchy", "Naming relationships", "Visual architecture", "Parent/sub-brand relationships", "Scalable identity system"],
      },
    ],
  },
  {
    id: "websites-setup",
    title: "Websites & Business Setup",
    tab: "Websites",
    services: [
      {
        id: "website",
        name: "The Website Edit",
        price: "Starting at $1,250",
        short: "A custom website designed as an extension of your brand.",
        desc: "A custom website designed as an extension of the brand — from a single editorial page to a full multi-page experience.",
        includes: null,
      },
      {
        id: "setup",
        name: "The Business Setup Edit",
        price: "Starting at $300",
        short: "Domains, email, phone and scheduling — connected and ready to run.",
        desc: "The operational foundation behind the brand, connected and ready to run.",
        includes: ["Domain connections", "Business email setup", "Branded email signatures", "Business phone connections", "Scheduling", "Inquiry forms", "Administrative account setup"],
      },
      {
        id: "experience",
        name: "The Client Experience Edit",
        price: "Custom quote",
        short: "Branded proposals, onboarding and every touchpoint between.",
        desc: "The touchpoints your clients move through — proposals, onboarding, and every branded interaction in between.",
        includes: null,
      },
    ],
  },
  {
    id: "design-creative",
    title: "Design & Creative",
    tab: "Creative",
    services: [
      {
        id: "book",
        name: "The Book & Publication Edit",
        price: "Custom quote",
        short: "Covers, devotionals and journals that carry the story.",
        desc: "Book covers, jackets, devotionals, journals and publications that carry the story before a page is turned.",
        includes: ["Front cover", "Back cover", "Spine direction", "Cover variants", "Production files"],
      },
      {
        id: "content",
        name: "The Content Edit",
        price: "Custom quote",
        short: "Ongoing branded content, campaigns and editorial assets.",
        desc: "Ongoing branded content — social, campaign and editorial assets that keep the brand visible and consistent.",
        includes: null,
      },
      {
        id: "creative",
        name: "The Creative Edit",
        price: "$200 / 90-minute session",
        short: "A focused session to refine an existing brand or concept.",
        desc: "You already have something. It just isn't clicking. A focused creative-direction session for an existing brand, logo, website, campaign, presentation, artist identity or concept that needs refinement, clarity or stronger visual direction.",
        includes: null,
      },
    ],
  },
];

const COMPLETE = {
  id: "complete",
  name: "The Complete Edit",
  price: "$6,500",
  short: "The comprehensive option — one seamless build, start to finish.",
  desc: "The comprehensive option — the full engagement from identity through website and business setup, delivered as one seamless build.",
  includes: null,
};

const ServiceCard = ({ service, category, onOpen }) => {
  const ps = PANEL_STYLES[category.id];
  return (
    <button
      data-testid={`edit-menu-item-${service.id}`}
      onClick={() => onOpen(service, category)}
      className="group w-full text-left border-t py-4 lg:py-5 last:border-b"
      style={{ borderColor: ps.rule }}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h4
          className="font-serif text-lg lg:text-xl tracking-tight group-hover:translate-x-1.5 transition-transform duration-500"
          style={{ color: ps.heading }}
        >
          {service.name}
        </h4>
        <span
          aria-hidden="true"
          className="font-serif group-hover:translate-x-1 transition-transform duration-500"
          style={{ color: ps.arrow }}
        >
          →
        </span>
      </div>
      <p className="mt-1 text-[13px] leading-relaxed" style={{ color: ps.text }}>
        {service.short}
      </p>
      <p
        className={`edit-menu-price mt-2 font-serif italic text-base ${ps.chromePrice ? "text-chrome-lavender" : ""}`}
        style={ps.chromePrice ? {} : { color: "#6d3f8e" }}
      >
        {service.price}
      </p>
    </button>
  );
};

const CategoryPanel = ({ category, index, onOpen }) => {
  const ps = PANEL_STYLES[category.id];
  return (
    <div
      data-testid={`edit-menu-column-${category.id}`}
      className="relative h-full overflow-hidden p-7 lg:p-9"
      style={{ background: ps.bg }}
    >
      {/* iridescent top edge */}
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-[3px] opacity-80"
        style={{ background: IRIDESCENT, backgroundSize: "200% auto" }}
      />
      <p className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: ps.eyebrow }}>
        {String(index + 1).padStart(2, "0")}
      </p>
      <h3 className="font-serif italic text-2xl lg:text-[1.7rem] tracking-tight" style={{ color: ps.heading }}>
        {category.title}
      </h3>
      <hr className="chrome-rule mt-4 mb-3 w-14" />
      {category.services.map((s) => (
        <ServiceCard key={s.id} service={s} category={category} onOpen={onOpen} />
      ))}
    </div>
  );
};

export const EditMenu = () => {
  const [activeTab, setActiveTab] = useState(CATEGORIES[0].id);
  const [selected, setSelected] = useState(null); // { service, category }

  const open = (service, category) => setSelected({ service, category });
  const activeIndex = CATEGORIES.findIndex((c) => c.id === activeTab);
  const activeCategory = CATEGORIES[activeIndex];

  return (
    <section id="menu" data-testid="edit-menu-section" className="px-6 md:px-12 lg:px-24 py-28 md:py-44 border-t border-charcoal/10">
      <Reveal>
        <p className="text-[11px] uppercase tracking-[0.34em] text-charcoal-mute mb-6">The Edit Menu</p>
        <h2 className="font-serif uppercase tracking-[-0.02em] leading-[0.92]">
          <span className="block text-xl md:text-3xl tracking-[0.12em] text-charcoal-soft">
            The Danielle Edit
          </span>
          <span className="block text-[12vw] md:text-[10.5vw] lg:text-[9.5vw]">
            Service
          </span>
          <span className="block text-[12vw] md:text-[10.5vw] lg:text-[9.5vw] italic text-chrome">
            Investment
          </span>
        </h2>
        <p className="mt-8 max-w-xl text-sm md:text-base text-charcoal-soft leading-relaxed">
          From a single identity to an entire brand world, every engagement is shaped around
          what the idea is becoming.
        </p>
      </Reveal>

      {/* Mobile: category tabs, one panel at a time */}
      <div className="md:hidden mt-14">
        <div data-testid="edit-menu-tabs" role="tablist" aria-label="Service categories" className="flex gap-2 mb-6">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              role="tab"
              aria-selected={activeTab === c.id}
              data-testid={`edit-menu-tab-${c.id}`}
              onClick={() => setActiveTab(c.id)}
              className={`flex-1 text-[10px] uppercase tracking-[0.16em] rounded-full px-3 py-2.5 border transition-colors duration-300 ${
                activeTab === c.id
                  ? "bg-charcoal text-paper border-charcoal"
                  : "border-charcoal/25 text-charcoal-soft hover:border-charcoal/50"
              }`}
            >
              {c.tab}
            </button>
          ))}
        </div>
        <div key={activeTab}>
          <CategoryPanel category={activeCategory} index={activeIndex} onOpen={open} />
        </div>
      </div>

      {/* Desktop: three color-block panels */}
      <div className="hidden md:grid md:grid-cols-3 md:gap-5 lg:gap-6 mt-20 items-stretch">
        {CATEGORIES.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.08} className="h-full">
            <CategoryPanel category={c} index={i} onOpen={open} />
          </Reveal>
        ))}
      </div>

      {/* The comprehensive option */}
      <Reveal delay={0.05}>
        <button
          data-testid="edit-menu-item-complete"
          onClick={() => open(COMPLETE, CATEGORIES[0])}
          className="group relative overflow-hidden mt-12 md:mt-14 w-full text-left rounded-sm px-7 py-8 md:px-12 md:py-10 bg-obsidian transition-transform duration-500 hover:scale-[1.005]"
        >
          <span
            aria-hidden="true"
            className="absolute top-0 left-0 right-0 h-[3px] opacity-80"
            style={{ background: IRIDESCENT, backgroundSize: "200% auto" }}
          />
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#cbb8dc]/70 mb-2">
                The comprehensive option
              </p>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-[#f6f0e6] group-hover:translate-x-1.5 transition-transform duration-500">
                {COMPLETE.name}
              </h3>
              <p className="mt-2 text-sm text-[#e9dff0]/75 leading-relaxed max-w-xl">{COMPLETE.short}</p>
            </div>
            <p className="edit-menu-price font-serif italic text-2xl md:text-3xl text-chrome-lavender shrink-0">
              {COMPLETE.price}
            </p>
          </div>
        </button>
      </Reveal>

      {/* Detail panel */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent
          data-testid="edit-menu-detail-dialog"
          className="bg-paper border-charcoal/15 max-w-lg p-8 md:p-10 gap-0"
        >
          {selected && (
            <>
              <p data-testid="edit-menu-detail-category" className="text-[10px] uppercase tracking-[0.3em] text-charcoal-mute mb-3">
                {selected.category.title}
              </p>
              <DialogHeader className="space-y-0">
                <DialogTitle data-testid="edit-menu-detail-name" className="font-serif text-3xl md:text-4xl tracking-tight font-normal">
                  {selected.service.name}
                </DialogTitle>
              </DialogHeader>
              <p data-testid="edit-menu-detail-price" className="font-serif italic text-xl text-[#6d3f8e] mt-3">
                {selected.service.price}
              </p>
              <hr className="chrome-rule my-6 w-16" />
              <DialogDescription data-testid="edit-menu-detail-desc" className="text-sm text-charcoal-soft leading-relaxed">
                {selected.service.desc}
              </DialogDescription>
              {selected.service.includes && (
                <ul data-testid="edit-menu-detail-includes" className="mt-5 space-y-2">
                  {selected.service.includes.map((inc) => (
                    <li key={inc} className="text-sm text-charcoal-soft leading-relaxed flex gap-2.5">
                      <span aria-hidden="true" className="text-[#c98da4]">·</span>
                      {inc}
                    </li>
                  ))}
                </ul>
              )}
              <button
                data-testid="edit-menu-detail-cta"
                onClick={() => {
                  setInquiryPrefill(selected.service.name);
                  setSelected(null);
                  scrollToHash("#inquire");
                }}
                className="group relative overflow-hidden mt-8 bg-charcoal text-paper text-[11px] uppercase tracking-[0.3em] rounded-full px-8 py-3.5 transition-transform duration-300 hover:scale-[1.03]"
              >
                <span className="relative z-10">Start Your Project</span>
                <span
                  aria-hidden="true"
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: "linear-gradient(100deg, #8fb3c4, #b3a3d4, #e3c2bd, #eed3ba, #9fc2d2)" }}
                />
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Reveal delay={0.1}>
        <div className="mt-20 md:mt-28 max-w-3xl">
          <p className="text-sm md:text-base text-charcoal-soft leading-relaxed max-w-xl">
            Published rates are introductory. Every brand has different needs — final investment
            is based on your project’s scope. Third-party subscriptions, domains, hosting, phone
            service, and printing are separate unless included in your proposal.
          </p>
          <p data-testid="edit-menu-payment-note" className="mt-5 text-sm md:text-base leading-relaxed max-w-xl">
            <span className="font-serif italic text-charcoal">Payment plans available for select projects.</span>{" "}
            <span className="text-charcoal-soft">Payment schedules are agreed upon before work begins.</span>
          </p>
          <hr className="chrome-rule my-12 max-w-sm" />
          <p className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.05] max-w-xl mb-8">
            Tell me what you’re building.{" "}
            <span className="italic text-chrome">We’ll find the right edit.</span>
          </p>
          <button
            data-testid="edit-menu-cta"
            onClick={() => scrollToHash("#inquire")}
            className="group relative overflow-hidden bg-charcoal text-paper text-[11px] uppercase tracking-[0.3em] rounded-full px-9 py-4 transition-transform duration-300 hover:scale-[1.03]"
          >
            <span className="relative z-10">Start Your Project</span>
            <span
              aria-hidden="true"
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{ background: "linear-gradient(100deg, #8fb3c4, #b3a3d4, #e3c2bd, #eed3ba, #9fc2d2)" }}
            />
          </button>
        </div>
      </Reveal>
    </section>
  );
};
